import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Calendar,
  Image,
  CheckSquare,
  BarChart3,
  Settings,
  Plus,
  Bell,
  Search,
  Filter,
  LogOut,
  Zap,
  Plug,
  FolderOpen
} from 'lucide-react';
import PostManager from './PostManager';
import ClientPortfolios from './ClientPortfoliosEnhanced';
import ContentCalendar from './ContentCalendarEnhanced';
import DesignWorkspace from './DesignWorkspace';
import QualityReview from './QualityReview';
import Analytics from './Analytics';
import TeamManagement from './TeamManagement';
import PluginManager from './PluginManagerEnhanced';
import ContentRepository from './ContentRepository';
import SEOManager from './SEOManager';
import { useAuthStore } from '../../store/authStore';

export default function EMHDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuthStore();
  const [notifications, setNotifications] = useState(3);

  const navigation = [
    { name: 'Dashboard', href: '/emh', icon: LayoutDashboard, roles: ['admin', 'account_manager'] },
    { name: 'Client Portfolios', href: '/emh/portfolios', icon: Users, roles: ['admin', 'account_manager', 'content_creator'] },
    { name: 'Content Repository', href: '/emh/repository', icon: FolderOpen, roles: ['admin', 'account_manager', 'content_creator', 'designer'] },
    { name: 'Content Calendar', href: '/emh/calendar', icon: Calendar, roles: ['admin', 'account_manager', 'content_creator', 'designer'] },
    { name: 'Post Manager', href: '/emh/posts', icon: Image, roles: ['admin', 'account_manager', 'content_creator'] },
    { name: 'Plugin Manager', href: '/emh/plugins', icon: Plug, roles: ['admin', 'account_manager'] },
    { name: 'SEO Manager', href: '/emh/seo', icon: Zap, roles: ['admin', 'account_manager'] },
    { name: 'Design Workspace', href: '/emh/design', icon: Image, roles: ['admin', 'designer'] },
    { name: 'Quality Review', href: '/emh/review', icon: CheckSquare, roles: ['admin', 'quality_reviewer'] },
    { name: 'Analytics', href: '/emh/analytics', icon: BarChart3, roles: ['admin', 'account_manager'] },
    { name: 'Team Management', href: '/emh/team', icon: Users, roles: ['admin'] },
    { name: 'Settings', href: '/emh/settings', icon: Settings, roles: ['admin'] }
  ];

  const userRole = 'admin';

  const filteredNavigation = navigation.filter(item =>
    item.roles.includes(userRole)
  );

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 bg-gradient-to-b from-gray-900 to-gray-800 border-r border-gray-700 shadow-xl">
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-2">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Edge Media Hub</h2>
              <p className="text-xs text-gray-400">EMH</p>
            </div>
          </div>
        </div>

        <nav className="mt-6 px-3 flex-1 overflow-y-auto">
          {filteredNavigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg mb-1 transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <item.icon
                  className={`mr-3 h-5 w-5 flex-shrink-0 ${
                    isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'
                  }`}
                />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Quick Actions */}
        <div className="mt-8 px-3 pb-6">
          <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Quick Actions
          </h3>
          <div className="mt-2 space-y-1">
            <button className="w-full flex items-center px-3 py-2 text-sm text-gray-300 rounded-lg hover:bg-gray-700 transition-colors">
              <Plus className="mr-3 h-4 w-4" />
              New Post
            </button>
            <button className="w-full flex items-center px-3 py-2 text-sm text-gray-300 rounded-lg hover:bg-gray-700 transition-colors">
              <Calendar className="mr-3 h-4 w-4" />
              Schedule Content
            </button>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-300 rounded-lg hover:bg-red-600 hover:text-white transition-colors"
            >
              <LogOut className="mr-3 h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search posts, clients, campaigns..."
                  className="pl-10 pr-4 py-2 w-80 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                />
              </div>
              <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <Filter className="h-5 w-5" />
              </button>
            </div>

            <div className="flex items-center space-x-4">
              <button className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <Bell className="h-5 w-5" />
                {notifications > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {notifications}
                  </span>
                )}
              </button>
              <div className="flex items-center space-x-3 pl-4 border-l border-gray-200">
                <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-md">
                  <span className="text-white text-sm font-bold">
                    {user?.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                  <p className="text-xs text-gray-500 capitalize">{userRole}</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          <Routes>
            <Route index element={<EMHOverview />} />
            <Route path="portfolios/*" element={<ClientPortfolios />} />
            <Route path="repository/*" element={<ContentRepository />} />
            <Route path="calendar/*" element={<ContentCalendar />} />
            <Route path="posts/*" element={<PostManager />} />
            <Route path="plugins/*" element={<PluginManager />} />
            <Route path="seo/*" element={<SEOManager />} />
            <Route path="design/*" element={<DesignWorkspace />} />
            <Route path="review/*" element={<QualityReview />} />
            <Route path="analytics/*" element={<Analytics />} />
            <Route path="team/*" element={<TeamManagement />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function EMHOverview() {
  const stats = [
    { name: 'Active Clients', value: '24', change: '+12%', changeType: 'positive' },
    { name: 'Posts This Month', value: '156', change: '+23%', changeType: 'positive' },
    { name: 'Pending Reviews', value: '8', change: '-15%', changeType: 'negative' },
    { name: 'Scheduled Posts', value: '42', change: '+8%', changeType: 'positive' },
  ];

  const recentActivity = [
    { action: 'Post approved', client: 'TechCorp', time: '2 minutes ago', type: 'approval' },
    { action: 'Design completed', client: 'Fashion Brand', time: '15 minutes ago', type: 'design' },
    { action: 'Post scheduled', client: 'Restaurant Chain', time: '1 hour ago', type: 'schedule' },
    { action: 'Campaign launched', client: 'Fitness Studio', time: '2 hours ago', type: 'campaign' },
  ];

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-2">
            <Zap className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Edge Media Hub Dashboard</h1>
        </div>
        <p className="text-gray-600 ml-14">Centralized social media content management platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <div key={stat.name} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
              </div>
              <span className={`text-sm font-semibold px-2 py-1 rounded-full ${
                stat.changeType === 'positive'
                  ? 'text-green-700 bg-green-100'
                  : 'text-red-700 bg-red-100'
              }`}>
                {stat.change}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                <div className={`w-2 h-2 rounded-full ${
                  activity.type === 'approval' ? 'bg-green-500' :
                  activity.type === 'design' ? 'bg-blue-500' :
                  activity.type === 'schedule' ? 'bg-yellow-500' :
                  'bg-purple-500'
                }`} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                  <p className="text-xs text-gray-500">{activity.client} • {activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Platform Performance */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Performance</h3>
          <div className="space-y-4">
            {[
              { platform: 'Instagram', posts: 45, engagement: '8.2%', color: 'bg-gradient-to-r from-pink-500 to-purple-500' },
              { platform: 'Facebook', posts: 32, engagement: '6.1%', color: 'bg-blue-600' },
              { platform: 'LinkedIn', posts: 28, engagement: '4.8%', color: 'bg-blue-700' },
              { platform: 'Twitter', posts: 51, engagement: '3.2%', color: 'bg-sky-500' },
            ].map((platform) => (
              <div key={platform.platform} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full ${platform.color}`} />
                  <span className="text-sm font-medium text-gray-900">{platform.platform}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">{platform.posts} posts</p>
                  <p className="text-xs text-gray-500">{platform.engagement} engagement</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
